/**
 * Résolution des fonds carte PMTiles selon le contexte d'affichage.
 *
 * - default : flat clair/sombre (thème UI) — pickers, mini-cartes
 * - map     : fond choisi (plat / topo / satellite) — /map et planification
 */
import { getApiBaseUrl, getApiVersion } from '../../../shared/api/runtimeConfig'
import type { AppColorScheme } from '../hooks/useAppColorScheme'
import type { MapBackground } from './mapTheme'

export type BasemapContext = 'default' | 'map'

export type BasemapStyleId =
  | 'pmtiles-flat-light'
  | 'pmtiles-flat-dark'
  | 'topo-light'
  | 'topo-dark'
  | 'raster-satellite'

export function flatStyleIdForScheme(scheme: AppColorScheme): BasemapStyleId {
  return scheme === 'dark' ? 'pmtiles-flat-dark' : 'pmtiles-flat-light'
}

export function topoStyleIdForScheme(scheme: AppColorScheme): BasemapStyleId {
  return scheme === 'dark' ? 'topo-dark' : 'topo-light'
}

export function styleIdForBackgroundOnMap(background: MapBackground): BasemapStyleId {
  switch (background) {
    case 'satellite':
      return 'raster-satellite'
    case 'outdoor-bright':
      return 'topo-light'
    case 'outdoor-dark':
      return 'topo-dark'
    case 'flat-dark':
      return 'pmtiles-flat-dark'
    case 'flat-bright':
    default:
      return 'pmtiles-flat-light'
  }
}

export function isSatelliteBackground(background: MapBackground | null | undefined): boolean {
  return background === 'satellite'
}

export function resolveBasemapStyleId(
  context: BasemapContext,
  scheme: AppColorScheme,
  mapBackground?: MapBackground | null,
): BasemapStyleId {
  if (context === 'map' && mapBackground) {
    return styleIdForBackgroundOnMap(mapBackground)
  }
  if (context === 'map') {
    return topoStyleIdForScheme(scheme)
  }
  return flatStyleIdForScheme(scheme)
}

export function getBasemapStyleUrl(styleId: BasemapStyleId | string): string {
  return `${getApiBaseUrl()}/${getApiVersion()}/map/style/${styleId}/`
}

export function resolveBasemapStyleUrl(
  context: BasemapContext,
  scheme: AppColorScheme,
  mapBackground?: MapBackground | null,
): string {
  return getBasemapStyleUrl(resolveBasemapStyleId(context, scheme, mapBackground))
}
